import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { plural } from '../shared/plural';
import type { ViaWritePreview } from './viaWriteApi';

/**
 * Okno „Eksportuj do TMS” (paczka p. Mariusza 21.10, BR-ADM-18; przełącznik
 * `tmsExportOneStep`, S-21.10-11): podgląd obu plików z wynikiem walidatora
 * i jedno „Potwierdź zapis” — zatwierdzenie i wytworzenie plików jedną
 * decyzją. Treść jest widoczna od razu (nie za „Pokaż treść”): człowiek
 * zatwierdza to, co widzi, a serwer przyjmuje potwierdzenie tylko dla tej
 * samej treści (sumy kontrolne z podglądu).
 */
export const TmsExportDialog = ({
  open,
  preview,
  duplicateBlock,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  preview: ViaWritePreview | null;
  /** Odmowa z kontroli duplikatu, dopóki nie ma „to nie duplikat”. */
  duplicateBlock: string | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const files =
    preview && (preview.files?.length ?? 0) > 0
      ? (preview.files ?? [])
      : preview
        ? [{ kind: 'MAIN', label: 'zlecenie', fileName: '', xml: preview.xml, errors: preview.errors }]
        : [];
  const blocked = preview?.exportBlockReason ?? null;
  // Wysyłka do TMS nie zadziała (25.10.2026, decyzja użytkownika 24.10.2026:
  // „jak nie działa to nie działa, daj tam errora po kliknięciu”) — błąd,
  // nie informacja: konfiguracja/poświadczenia albo tryb katalogu.
  const deliveryBlock = preview?.deliveryBlockReason ?? null;
  const directoryOnly = preview?.delivery?.reachesTms === false && deliveryBlock === null;
  const canConfirm = !!preview && preview.canExport === true && duplicateBlock === null && !busy;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth data-testid="tms-export-dialog">
      <DialogTitle>Eksport do TMS — podgląd {files.length > 1 ? `${files.length} plików` : 'pliku'}</DialogTitle>
      <DialogContent dividers>
        {preview === null ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              „Potwierdź zapis” zatwierdza te {files.length > 1 ? 'pliki' : 'dane'} i od razu je wytwarza — ta sama
              decyzja co dotąd „Zatwierdź” i „Wytwórz plik”. Zapisujemy, kto zatwierdził i kiedy. Zmiana danych po
              zapisie cofa zatwierdzenie.
            </Typography>
            {preview.delivery && (
              <Chip
                size="small"
                variant="outlined"
                color={preview.delivery.target === 'prod' ? 'warning' : 'default'}
                label={`Dostarczanie: ${preview.delivery.label}`}
                sx={{ mb: 1 }}
              />
            )}
            {directoryOnly && (
              <Alert severity="error" sx={{ mb: 1 }} data-testid="tms-export-not-tms">
                <AlertTitle>Wysyłka do TMS nie jest włączona</AlertTitle>
                Plik zostanie zapisany tylko w katalogu aplikacji i nie trafi do TMS. Po „Potwierdź zapis” zlecenie
                dostanie stan „Błąd zapisu do VIA” z tym powodem — zgłoś to administratorowi.
              </Alert>
            )}
            {duplicateBlock && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="tms-export-duplicate-block">
                {duplicateBlock}
              </Alert>
            )}
            {preview.errors.length > 0 ? (
              <Alert severity="error" sx={{ mb: 1 }} data-testid="tms-export-errors">
                <AlertTitle>
                  Importer odrzuci {files.length > 1 ? 'pliki' : 'ten plik'} — brakuje{' '}
                  {plural(preview.errors.length, 'pola', 'pól', 'pól')}
                </AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {preview.errors.map((e) => (
                    <li key={e}>
                      <Typography variant="body2">{e}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 1 }}>
                {files.length > 1 ? 'Pliki mają' : 'Plik ma'} komplet pól wymaganych przez importer.
              </Alert>
            )}
            {preview.ostrzezenia.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <AlertTitle>Do sprawdzenia ({preview.ostrzezenia.length})</AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {preview.ostrzezenia.map((o) => (
                    <li key={o}>
                      <Typography variant="body2">{o}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {blocked && preview.errors.length === 0 && (
              <Alert
                severity={blocked === deliveryBlock ? 'error' : 'info'}
                sx={{ mb: 1 }}
                data-testid="tms-export-block"
              >
                {blocked === deliveryBlock ? <AlertTitle>Wysyłka do TMS nie zadziała</AlertTitle> : null}
                {blocked}
              </Alert>
            )}
            {files.map((f) => (
              <Box key={f.kind} sx={{ mt: 1 }} data-testid={`tms-export-file-${f.kind}`}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {f.label}
                  {f.fileName ? ` — ${f.fileName}` : ''} ·{' '}
                  {f.errors.length === 0 ? 'komplet pól' : `brakuje ${plural(f.errors.length, 'pola', 'pól', 'pól')}`}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    maxHeight: 260,
                    overflow: 'auto',
                    fontSize: 12,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {f.xml}
                </Box>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!canConfirm}
          data-testid="adm-eksport-potwierdz-zapis"
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Potwierdź zapis
        </Button>
      </DialogActions>
    </Dialog>
  );
};
